/**
 * THE SHAPE-BINDING OBJECT — `/api/es/ticks` payload → `TickProbe`.
 *
 * DESK-n8 ruled the rung machine be built CONTRACT-FIRST and the wire binding kept SEPARATE:
 * *"the rungs are SEMANTIC STATES, the response shape is a WIRE FORMAT, and the rungs outlive every
 * wire format that carries them; shape binding = separate keyed object, one blast radius."*
 * **This file is that blast radius.** `tickState.ts` imports nothing from here and must not.
 *
 * ⛔⛔ BINDING INSTRUCTION FROM DESK-n8 (18:40:37Z), AND IT IS WHY `feed_state` IS ABSENT BELOW:
 * *"do NOT bind rung 2/2b to `feed_state` — derive ingest-liveness from the payload's OWN
 * newest/age fields, which carry row-level provenance."*
 * The route's `feed_state` is `allSchemaTotal > 0 ? 'PARTIAL_FEED' : 'IDLE'` over an UNBOUNDED
 * count ⇒ monotonic and irreversible; `IDLE` is reachable exactly once in the table's lifetime and
 * the field can never say the ingest died. A cure is minted (pen dd, key GK-q).
 * ⇒ ⭐ **THE LAW THE DESK BANKED FROM IT, AND IT GENERALISES PAST THIS FIELD: *A STATE MUST BE
 * RE-EARNED EVERY WINDOW, NEVER INHERITED FROM HISTORY.* An all-time count is a fact about the
 * PAST; a state field is a claim about NOW.**
 */
import type { TickProbe } from "./tickState";

/** The subset of `/api/es/ticks` this adapter reads. The route serves more; we bind only what we use. */
export interface TicksPayload {
  ok?: boolean;
  state?: string;
  root?: string;
  /** ⛔ POOLS GENERATIONS. The route labels it NOT_A_TICK_COUNT and it must never reach a reader. */
  rows?: number;
  /** Content-bearing rows — `side IS NOT NULL`. THIS is the population a derivation can read. */
  content_rows?: number;
  content_state?: "NO_CONTENT_YET" | "CONTENT_PRESENT" | string;
  count_label?: string;
  /** The newest content row's price, ALREADY divided by 1e-9 at read by the route. Never re-scale. */
  last_price?: number | null;
  /** Age of the newest CONTENT-bearing row, seconds — row-level provenance, the desk's mandated source. */
  last_price_age_s?: number | null;
  last_price_vendor_ts?: string | null;
  /** Vendor per-stream sequence OF THE NEWEST CONTENT ROW — population-scoped by construction. */
  last_price_vendor_sequence?: number | string | null;
  budget_s?: number;
  served_at?: string;
  bars?: { t: string; rows: number; content_rows: number; last_price: number | null }[];
  cvd?: number | null;
  cvd_state?: string;
}

/**
 * How stale the newest CONTENT row may be before we stop calling the ingest live.
 * ⚠ This is a LIVENESS threshold, not the panel's freshness budget — they answer different
 * questions and are deliberately separate numbers even when they happen to agree.
 */
export const INGEST_LIVE_MAX_AGE_S = 90;

/**
 * Map a payload to a probe. `null` payload = the fetch did not produce one.
 *
 * ⛔ EVERY COUNT HERE IS THE **CONTENT** COUNT, NEVER `rows`. The route ships `count_label:
 * NOT_A_TICK_COUNT` precisely because `rows` pools the legacy and content generations — 244,160 vs
 * 44,003 on the live tape at 18:46Z, a 5.5x gap. A pane binding `rows` would render a plausible
 * number ~5x too large with nothing anywhere to contradict it.
 */
export function toProbe(
  payload: TicksPayload | null,
  opts: { windowS: number; population: string; nowMs?: number },
): TickProbe {
  const { windowS, population } = opts;
  const nowMs = opts.nowMs ?? Date.now();

  // No payload at all — NOT an empty store. `null` on every count so the ladder grades it NO FEED
  // rather than manufacturing a zero (`Number(null)` is 0, which is the whole hazard).
  if (payload === null || payload.ok !== true) {
    return {
      reachable: false,
      rowsTotal: null,
      rowsInWindow: null,
      watermark: null,
      fieldsPresent: [],
      ingestLive: false,
      asOfMs: nowMs,
      asOfSource: "client",
      windowS,
      population,
      newestRowMs: null,
    };
  }

  const contentTotal = num(payload.content_rows);
  const ageS = num(payload.last_price_age_s);

  /*
    INGEST LIVENESS — from the DATA's own newest row, per the desk's binding instruction and per
    GK-n's serve-leg condition. Never from `feed_state`, never from process/deploy liveness.
    ⚠ `null` age is NOT live: an unreported age is an unknown, and an unknown must not resolve to
    the reassuring branch. That is the direction every instrument failed in today.
  */
  const ingestLive = ageS !== null && ageS <= INGEST_LIVE_MAX_AGE_S;

  /*
    FIELDS PRESENT — from `content_state`, which is the route's own row-level assertion that
    `side IS NOT NULL` holds for at least one row. We report PRESENCE only.
    ⛔ We do NOT assert what the fields MEAN: meaning lives in the producer's contract, and a panel
    that claims it has no instrument that would ever notice being wrong.
  */
  const fieldsPresent = payload.content_state === "CONTENT_PRESENT" ? ["side", "size", "price"] : [];

  /*
    WATERMARK — MUST be population-scoped (`max(id) WHERE <population>`, never bare). We use the
    vendor sequence OF THE NEWEST CONTENT ROW, which is population-scoped by construction because
    the route takes it from the same row as `last_price`.
    ⇒ bb measured why a bare counter is unusable: the old and new writers interleaved 9.091s on one
      shared id counter, and dd proved 143 content rows sit INSIDE the legacy id range ⇒ there is no
      cut point at all. A monotonic counter ORDERS rows; it does not SEPARATE writers.
  */
  const watermark = num(payload.last_price_vendor_sequence);

  // Rows inside the pane's window, counted from the bars the route returned — content only.
  const cutoffMs = nowMs - windowS * 1000;
  const rowsInWindow =
    payload.bars === undefined
      ? null
      : payload.bars
          .filter((b) => Date.parse(b.t) >= cutoffMs)
          .reduce((n, b) => n + (Number(b.content_rows) || 0), 0);

  return {
    reachable: true,
    rowsTotal: contentTotal,
    rowsInWindow,
    watermark,
    fieldsPresent,
    ingestLive,
    // ⛔ THE SOURCE TRAVELS WITH THE STAMP. A consumer that cannot tell a server-stated instant from
    // a client-inferred one cannot know whether a clock skew is in play — and it will not find out.
    asOfMs: payload.served_at ? Date.parse(payload.served_at) : nowMs,
    asOfSource: payload.served_at ? "server" : "client",
    windowS,
    population,
    newestRowMs: payload.last_price_vendor_ts ? Date.parse(payload.last_price_vendor_ts) : null,
  };
}

/**
 * Is a CVD series derivable from this payload at all?
 *
 * ⛔ TODAY THE ANSWER IS NO AND THE ROUTE SAYS SO ITSELF: `cvd_state: "ABSENT"`, because the live
 * CVD is BUILDER-z's aggregator and is not derived at this endpoint. **The pane must render that as
 * its own fact and never as a zero** — an absent CVD and a zero CVD are different facts and only
 * one of them is a trading signal.
 */
export function cvdAvailable(payload: TicksPayload | null): boolean {
  return payload?.cvd_state === "PRESENT" && payload.cvd !== null && payload.cvd !== undefined;
}

/** Numeric coercion that keeps `null` as `null`. `Number(null)` is 0, and a confident zero is the enemy. */
function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * The PRICE REGISTER — the owner's "the price must feel live", answered from data already on the wire.
 *
 * ⛔⛔ THE MINT ATTRIBUTED THE SLOW PRICE TO A POLL RATE. IT IS NOT A POLL RATE. Measured on both live
 * routes, 8s apart, three times: the price the owner watches is `EsChartPanel`'s `last?.c` — THE CLOSE
 * OF THE LAST 1-MINUTE BAR — and it changed only when the bar ROLLED (…920000 close 7756.25 twice,
 * then …980000 close 7756.00), while the TICK last_price moved 7756.75 → 7756.50 at ages 4s/13s/1s.
 * ⇒ ***A 1-MINUTE BAR'S CLOSE CHANGES ONCE PER MINUTE BY CONSTRUCTION. NO POLL RATE FIXES IT — a 3s
 * poll of a 1m-bar endpoint returns the same number twenty times.*** The owner is watching the BAR
 * INTERVAL, not the poll interval, so the cure is a RENDER (a tick-level price) before it is a cadence.
 *
 * ⭐ `last_price` is ALREADY scaled at read by the route (`PRICE_SCALE` 1e-9). ⛔ NEVER divide again
 * here: a consumer-side rescue of a producer's scaling makes the producer's defect unobservable at the
 * only layer that could report it — and 1e9 is the one magnitude error an owner spots instantly.
 */
export interface PriceRegister {
  /** Scaled price, or null when the route reported none. `null` is ABSENT, never 0. */
  price: number | null;
  /** Age of THAT row, seconds — its own provenance, not the response's. */
  ageS: number | null;
  /** The vendor timestamp of the same row the price came from. */
  vendorTs: string | null;
  /**
   * Does the price still deserve to be read as current?
   * ⚠ A FAST NUMBER THAT SILENTLY FREEZES IS WORSE THAN A SLOW ONE THAT ADMITS ITS AGE — so this is
   * derived from the row's own age, and an UNREPORTED age is NOT fresh.
   */
  fresh: boolean;
}

/**
 * Seconds after which the price register stops claiming currency.
 *
 * ⛔ THIS CONSTANT *IS* THE PRODUCT DECISION, AND GK-s proved nothing asserted it: mutating it
 * 10 → 1000 passed every gate, so **a 16-MINUTE-OLD PRICE WOULD STILL CLAIM TO BE CURRENT**.
 * ⇒ ***A BARE THRESHOLD IS THE ONE VALUE A SUITE NEVER CHECKS, BECAUSE IT LOOKS LIKE CONFIGURATION
 * AND BEHAVES LIKE LOGIC.*** It is bounded by an arm now, with the reasoning attached:
 *   FLOOR 2s ... below the observed tick cadence the register would flicker to NOT CURRENT on a
 *                healthy tape, which is fail-loud theatre and trains the reader to ignore it
 *   CEILING 30s  the owner asked for a price that feels LIVE; a number older than half a minute
 *                is not that, whatever the panel's 90s feed budget says
 */
export const PRICE_FRESH_MAX_AGE_S = 10;
/** The defensible band for the freshness threshold. An arm asserts the constant sits inside it. */
export const PRICE_FRESH_BOUNDS = { minS: 2, maxS: 30 } as const;

export function toPriceRegister(
  payload: TicksPayload | null,
  maxAgeS: number = PRICE_FRESH_MAX_AGE_S,
): PriceRegister {
  if (payload === null || payload.ok !== true) {
    return { price: null, ageS: null, vendorTs: null, fresh: false };
  }
  const price = num(payload.last_price);
  const ageS = num(payload.last_price_age_s);
  return {
    price,
    ageS,
    vendorTs: payload.last_price_vendor_ts ?? null,
    // unreported age => NOT fresh. An unknown must not resolve to the reassuring branch.
    fresh: price !== null && ageS !== null && ageS <= maxAgeS,
  };
}

/**
 * The CVD REGISTER, contract-first — built BEFORE the series exists, which is exactly what the
 * contract-first split was paid for (DESK-n8: *"h wires the pane's CVD register when the series
 * exists — a small keyed act"*).
 *
 * ⛔⛔ THREE STATES, NOT TWO, AND THE DISTINCTION IS THE WHOLE OBJECT:
 *   ABSENT ...... no series. TODAY's state — the route says `cvd_state: "ABSENT"` itself, because the
 *                 aggregator is BUILDER-dd's and is not derived at this endpoint.
 *   ZERO ........ a series exists and its value is 0. ⭐ THAT IS A MEASUREMENT — balanced flow.
 *   VALUE ....... a series exists and is non-zero.
 * ⇒ ***AN ABSENT CVD AND A ZERO CVD ARE DIFFERENT FACTS AND ONLY ONE OF THEM IS A TRADING SIGNAL.***
 * Rendering absent-as-zero draws a flat line that asserts balanced flow the data never claimed — the
 * first defect recorded in this seat's inheritance, and the reason the pane exists at all.
 */
export type CvdState = "ABSENT" | "ZERO" | "VALUE";

export function cvdRegister(payload: TicksPayload | null): { state: CvdState; value: number | null } {
  if (!cvdAvailable(payload)) return { state: "ABSENT", value: null };
  const v = num(payload!.cvd);
  if (v === null) return { state: "ABSENT", value: null };
  return { state: v === 0 ? "ZERO" : "VALUE", value: v };
}
